import React, { useState, useMemo, useEffect } from "react";
import { Box, TextField, List, ListItemButton, Checkbox, Button, InputAdornment, Divider, Typography } from "@mui/material";
import "../../styles/common.css";
import SearchIcon from "@mui/icons-material/Search";


type CustomDropdownProps<T> = {
  items: T[];
  value: T | null | T[];
  onChange: (item: T | null | T[] | null) => void;
  placeholder?: string;
  maxHeight?: number;
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;
  filterFn?: (item: T, search: string) => boolean;
  showSearch?: boolean;
  hideList?: boolean;
  multiple?: boolean;
  getKey?: (item: T) => string; // ✅ new prop for stable comparison
  showAllWhenNoSearch?: boolean;
  groupBy?: (item: T) => string;
  isSubscriber?: boolean;
  onClose?: () => void;
  forceShowList?: boolean;
};

function CustomDropdown<T>({
  items,
  value,
  onChange,
  placeholder = "Search...",
  maxHeight = 300,
  renderItem,
  filterFn,
  showSearch = true,
  hideList = false,
  multiple = false,
  getKey,
  groupBy,
  isSubscriber = false,
  showAllWhenNoSearch = false,
  onClose,
  forceShowList,
}: CustomDropdownProps<T>) {
  const [search, setSearch] = useState("");
  const [tempSelected, setTempSelected] = useState<T[]>(() =>
    Array.isArray(value) ? value : value ? [value] : []
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 250);

    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (Array.isArray(value)) setTempSelected(value);
    else if (value) setTempSelected([value]);
    else setTempSelected([]);
  }, [value]);

  const isEqual = (a: T | null | undefined, b: T | null | undefined) => {
    if (!a || !b) return false;
    if (getKey) return getKey(a) === getKey(b);
    return a === b;
  };

  const RESULT_CAP = 50; // prevent rendering thousands

  const filteredItems = useMemo(() => {
    const search = debouncedSearch;
    const results: T[] = [];
    for (const item of items) {
      const isSelected = tempSelected.some((i) => isEqual(i, item));

      // Always include selected items
      if (multiple && isSelected) {
        results.push(item);
        continue;
      }

      // No search behavior
      if (!search) {
        if (forceShowList || showAllWhenNoSearch) {
          results.push(item);
        }
        continue;
      }

      // Search match
      const match = filterFn ? filterFn(item, search) : true;
      if (match) results.push(item);

      // Cap results early (huge perf gain)
      if (results.length >= RESULT_CAP) break;
    }

    return results;
  }, [
    items,
    debouncedSearch,
    filterFn,
    tempSelected,
    multiple,
    showAllWhenNoSearch,
    forceShowList,
  ]);

  const grouped = useMemo(() => {
    if (!isSubscriber || !groupBy) return filteredItems;
    return filteredItems.reduce((acc: any, item: T) => {
      const group = groupBy(item) || "Other";
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {});
  }, [filteredItems, groupBy, isSubscriber]);

  const handleItemClick = (item: T) => {
    if (multiple) {
      const exists = tempSelected.some((i) => isEqual(i, item));
      if (exists) {
        setTempSelected(tempSelected.filter((i) => !isEqual(i, item)));
      } else {
        setTempSelected([...tempSelected, item]);
      }
    } else {
      onChange(item);
      onClose?.();
    }
  };

  const handleAddClick = () => {
    onChange(tempSelected);
    onClose?.();
  };
  const hasChanges = useMemo(() => {
    if (!multiple) return false;
    const current = Array.isArray(value) ? value : value ? [value] : [];
    if (current.length !== tempSelected.length) return true;
    const allMatch = current.every((v) =>
      tempSelected.some((t) => isEqual(v, t))
    );
    return !allMatch;
  }, [tempSelected, value, multiple]);

  return (
    <Box
      className="custom-dropdown"
      sx={{ minWidth: 200, maxHeight }}
      >
      {/* Search bar */}
      {showSearch && (
        <Box className="custom-dropdown__search">
          <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            className="custom-dropdown__search-input"
            sx={{
              borderBottom: (!hideList && filteredItems.length === 0) && !multiple ? "" : "2px solid #F2F2F4",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="cf-search-icon" />
                </InputAdornment>
              ),
            }}
            />
        </Box>
      )}

      {!hideList && (filteredItems.length > 0 || forceShowList) && (
        <Box
          className="custom-dropdown__list-container"
          sx={{
            maxHeight: maxHeight - (showSearch ? 48 : 0),
            overflowY: "auto",
            borderBottom: "1px solid #ddd",
          }}
        >
        <List disablePadding>
          {Array.isArray(grouped)
            ? grouped?.map((item: T, idx: number) => {
                const isSelected = multiple
                  ? tempSelected.some((i) => isEqual(i, item))
                  : isEqual(value as T, item);

                return (
                  <ListItemButton
                    key={idx}
                    selected={isSelected}
                    onClick={() => handleItemClick(item)}
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    className="custom-dropdown__list"
                  >
                    <Box sx={{ flex: 1 }}>{renderItem(item, isSelected)}</Box>
                    {multiple && <Checkbox className="cf-checkbox" checked={isSelected} size="small" sx={{ p: 0 }} />}
                  </ListItemButton>
                );
              })

            : // Subscriber: Grouped
              Object.keys(grouped)
                .sort((a, b) => {
                  if (a === "Spaces") return -1;
                  if (b === "Spaces") return 1;
                  return 0;
                })
                .map((group, gi) => (
                <React.Fragment key={group}>
                  <Typography
                    sx={{
                      px: 1,
                      py: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#777",
                    }}
                  >
                    {group}
                  </Typography>

                  {grouped[group].map((item: T, idx: number) => {
                    const isSelected = multiple
                      ? tempSelected.some((i) => isEqual(i, item))
                      : isEqual(value as T, item);

                    return (
                      <ListItemButton
                        key={`${group}-${idx}`}
                        selected={isSelected}
                        onClick={() => handleItemClick(item)}
                        sx={{ display: "flex", justifyContent: "space-between" }}
                      >
                        <Box sx={{ flex: 1 }}>{renderItem(item, isSelected)}</Box>
                        {multiple && <Checkbox className="cf-checkbox" checked={isSelected} size="small" sx={{ p: 0 }} />}
                      </ListItemButton>
                    );
                  })}

                  {gi !== Object.keys(grouped).length - 1 && <Divider sx={{ my: 0.5 }} />}
                </React.Fragment>
              ))}
          </List>
        </Box>
      )}
      {multiple && (
          <Button
            className={`cf-multi-apply-btn ${!hasChanges ? "disabled" : ""}`} 
            disabled={!hasChanges} 
            onClick={handleAddClick}
            sx={{
              mt: 1,
              mx: 1,
              mb: 1,
            }}
          >
            Add
          </Button>
      )}
    </Box>
  );
}

export default CustomDropdown;
